import { useEffect, useState } from 'react';
import { Button, Input, List, Modal, Space, Typography, message } from 'antd';
import { CopyOutlined, DeleteOutlined, ShareAltOutlined, UserAddOutlined } from '@ant-design/icons';
import {
  getGraphShares,
  shareGraphWithUser,
  unshareGraphWithUser,
} from '../services/api';

const { Text } = Typography;

export default function ShareButton({ jobId, canManage, publicAccess = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [details, setDetails] = useState({ owner: null, shared_with: [], can_manage: false });
  const [loading, setLoading] = useState(false);
  const shareId = String(jobId || '').split('-').pop();
  const shareUrl = `${window.location.origin}/graph/${shareId}`;
  const isPublic = details.public_access ?? publicAccess;

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    getGraphShares(jobId)
      .then(setDetails)
      .catch((error) => message.error(error.message))
      .finally(() => setLoading(false));
  }, [isOpen, jobId]);

  const addUser = async () => {
    const value = username.trim();
    if (!value) return;
    setLoading(true);
    try {
      setDetails(await shareGraphWithUser(jobId, value));
      setUsername('');
      message.success(`Graph shared with ${value}.`);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (value) => {
    setLoading(true);
    try {
      setDetails(await unshareGraphWithUser(jobId, value));
      message.success(`Access removed for ${value}.`);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      message.success('Public graph link copied.');
    } catch (error) {
      message.error('Could not copy the link. Select and copy it manually.');
    }
  };

  return (
    <>
      <Button icon={<ShareAltOutlined />} onClick={() => setIsOpen(true)}>
        Share
      </Button>
      <Modal
        title="Share graph"
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          {details.owner && (
            <Text type="secondary">
              Owner: {details.owner_display_name || details.owner}
            </Text>
          )}
          {isPublic && (
            <Space.Compact style={{ width: '100%' }}>
              <Input value={shareUrl} readOnly aria-label="Public graph link" />
              <Button icon={<CopyOutlined />} onClick={copyPublicLink}>
                Copy link
              </Button>
            </Space.Compact>
          )}
          {details.owner && (canManage || details.can_manage) && (
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                onPressEnter={addUser}
                placeholder="Username"
              />
              <Button type="primary" icon={<UserAddOutlined />} onClick={addUser} loading={loading}>
                Add
              </Button>
            </Space.Compact>
          )}
          {details.owner && (canManage || details.can_manage) && (
            <List
              loading={loading}
              dataSource={details.shared_with_users || (details.shared_with || []).map((name) => ({
                username: name,
                display_name: name,
              }))}
              locale={{ emptyText: 'This graph has not been shared with another user.' }}
              renderItem={(sharedUser) => (
                <List.Item
                  actions={[
                    <Button
                      key="remove"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeUser(sharedUser.username)}
                    >
                      Remove
                    </Button>,
                  ]}
                >
                  {sharedUser.display_name || sharedUser.username}
                </List.Item>
              )}
            />
          )}
        </Space>
      </Modal>
    </>
  );
}
